// src/hooks/usePushNotifications.js
// Unified push notification hook for iOS, Android (Capacitor) and Web (Firebase).
// Handles token registration, permission requests, and notification tap navigation.
//
// Key design decisions:
//   • Does NOT auto-prompt for permission on mount — the bell toggle triggers that.
//   • Auto-registers silently on mount only if permission was ALREADY granted and
//     the user hasn't explicitly toggled notifications off (backward-compat for
//     existing users who granted permission before the bell toggle existed).
//   • Exposes registerPush / unregisterToken / isRegistered so the bell toggle
//     in useNotificationPermission can drive the flow.
import { useState, useEffect, useRef, useCallback } from 'react';
import { isNativeApp } from '../config';
import api from '../services/api';

const PREF_KEY = 'nidoux_notifications_pref';

/**
 * Wait for the FCM token injected by the native iOS AppDelegate.
 * The native side sets window.__FCM_TOKEN__ and dispatches a 'fcmTokenReceived' event.
 * Returns the token string or null on timeout.
 * Accepts an AbortSignal to clean up the listener on unmount.
 */
function waitForFCMToken(timeoutMs = 10000, signal) {
  return new Promise((resolve) => {
    // Token may already be set (e.g. injected before this listener was attached)
    if (window.__FCM_TOKEN__) {
      resolve(window.__FCM_TOKEN__);
      return;
    }

    let settled = false;
    const settle = (value) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('fcmTokenReceived', handler);
      clearTimeout(timer);
      if (signal) signal.removeEventListener('abort', onAbort);
      resolve(value);
    };

    const handler = (e) => {
      settle(e.detail?.token || window.__FCM_TOKEN__ || null);
    };

    const onAbort = () => settle(null);

    const timer = setTimeout(() => {
      // Last chance: check the global in case the event was missed
      settle(window.__FCM_TOKEN__ || null);
    }, timeoutMs);

    window.addEventListener('fcmTokenReceived', handler);
    if (signal) {
      if (signal.aborted) { settle(null); return; }
      signal.addEventListener('abort', onAbort);
    }
  });
}

/**
 * @param {Object} options
 * @param {string} options.userId - Current logged-in user ID
 * @param {Function} options.onNotificationTap - Called when user taps a notification: (data) => void
 */
export function usePushNotifications({ userId, onNotificationTap }) {
  const tokenRef = useRef(null);
  const [isRegistered, setIsRegistered] = useState(
    () => localStorage.getItem(PREF_KEY) === 'enabled'
  );
  const registeredRef = useRef(false);
  const webListenerRef = useRef(false);
  // Tracks the in-flight auto-registration promise so manual toggle can
  // wait for it instead of racing with a second PushNotifications.register() call.
  const autoRegisterPromiseRef = useRef(null);
  // AbortController for cleaning up waitForFCMToken / Android polling on unmount
  const abortRef = useRef(null);

  // Send token to backend (with dedup — skip if same token already sent)
  const sendToken = useCallback(async (token, platform) => {
    if (!token || !userId) return;
    if (tokenRef.current === token && registeredRef.current) return;
    tokenRef.current = token;
    try {
      await api.request('/users/register-push-token', {
        method: 'POST',
        body: JSON.stringify({ token, platform }),
      });
      registeredRef.current = true;
      setIsRegistered(true);
      localStorage.setItem(PREF_KEY, 'enabled');
      console.log('Push token registered with backend for platform:', platform);
    } catch (err) {
      console.error('Failed to register push token:', err);
    }
  }, [userId]);

  // Remove token from backend. The bell's opt-out records the choice so the
  // app does not re-register on its own; logout passes { keepPreference: true }:
  // the device stops receiving this account's pushes, and the next login
  // registers again unless the user had switched notifications off.
  const unregisterToken = useCallback(async ({ keepPreference = false } = {}) => {
    // If auto-registration is still in flight, wait for it so we have a token to unregister
    if (!tokenRef.current && autoRegisterPromiseRef.current) {
      await autoRegisterPromiseRef.current;
    }
    if (!tokenRef.current) {
      // No token available — still update the local state so the bell reflects "off"
      registeredRef.current = false;
      setIsRegistered(false);
      if (!keepPreference) localStorage.setItem(PREF_KEY, 'disabled');
      return;
    }
    try {
      await api.request('/users/unregister-push-token', {
        method: 'POST',
        body: JSON.stringify({ token: tokenRef.current }),
      });
    } catch (err) {
      console.error('Failed to unregister push token:', err);
    }
    tokenRef.current = null;
    registeredRef.current = false;
    setIsRegistered(false);
    if (!keepPreference) localStorage.setItem(PREF_KEY, 'disabled');
  }, []);

  // Set up the web foreground message listener (called once)
  const setupWebForegroundListener = useCallback(async () => {
    if (webListenerRef.current) return;
    try {
      const { initializeApp } = await import('firebase/app');
      const { getMessaging, onMessage } = await import('firebase/messaging');
      const firebaseConfig = (await import('../firebaseConfig')).default;

      const app = initializeApp(firebaseConfig);
      const messaging = getMessaging(app);

      onMessage(messaging, (payload) => {
        console.log('Web push received in foreground:', payload);
        if (payload.notification) {
          const notif = new Notification(payload.notification.title, {
            body: payload.notification.body,
            icon: '/icons/icon-192x192.webp',
            data: payload.data,
          });
          notif.onclick = () => {
            window.focus();
            if (payload.data && onNotificationTap) {
              onNotificationTap(payload.data);
            }
            notif.close();
          };
        }
      });
      webListenerRef.current = true;
    } catch (err) {
      console.error('Error setting up web foreground listener:', err);
    }
  }, [onNotificationTap]);

  // ── Manual registration — called from bell toggle to enable ────────
  const registerPush = useCallback(async () => {
    if (!userId) return false;

    // If auto-registration is already in flight, wait for it instead of racing
    if (autoRegisterPromiseRef.current) {
      await autoRegisterPromiseRef.current;
      if (registeredRef.current) return true;
    }

    try {
      if (isNativeApp()) {
        const { PushNotifications } = await import('@capacitor/push-notifications');

        // Check / request permission
        let permResult = await PushNotifications.checkPermissions();
        if (permResult.receive === 'prompt') {
          permResult = await PushNotifications.requestPermissions();
        }

        if (permResult.receive === 'denied') {
          // Already denied — open OS settings so user can toggle manually
          try {
            const { NativeSettings, AndroidSettings, IOSSettings } = await import('capacitor-native-settings');
            const platform = window.Capacitor?.getPlatform?.();
            if (platform === 'ios') {
              await NativeSettings.open({ optionIOS: IOSSettings.App });
            } else {
              await NativeSettings.open({ optionAndroid: AndroidSettings.AppNotification });
            }
          } catch {
            try {
              const { App } = await import('@capacitor/app');
              await App.openUrl({ url: 'app-settings:' });
            } catch {
              console.warn('Cannot open native settings — no settings plugin available');
            }
          }
          return false;
        }

        if (permResult.receive !== 'granted') return false;

        const platform = window.Capacitor?.getPlatform?.() === 'ios' ? 'ios' : 'android';

        // Trigger native registration — the registration listener from the
        // mount effect will fire for Android; iOS uses the AppDelegate bridge.
        await PushNotifications.register();

        if (platform === 'ios') {
          const fcmToken = await waitForFCMToken(10000, abortRef.current?.signal);
          if (fcmToken) {
            await sendToken(fcmToken, 'ios');
            return true;
          }
          return false;
        }

        // Android — the registration listener set up in the mount effect
        // calls sendToken automatically. Wait for it to complete.
        // Uses AbortSignal so the interval is cleaned up if the component unmounts.
        const signal = abortRef.current?.signal;
        return new Promise((resolve) => {
          const check = setInterval(() => {
            if (registeredRef.current || signal?.aborted) {
              clearInterval(check);
              resolve(registeredRef.current);
            }
          }, 200);
          setTimeout(() => { clearInterval(check); resolve(registeredRef.current); }, 10000);
        });
      } else {
        // ── Web ──
        if (!('Notification' in window)) return false;
        if (Notification.permission === 'denied') return false;

        // This shows the browser permission prompt if not yet granted
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return false;

        const { initializeApp } = await import('firebase/app');
        const { getMessaging, getToken } = await import('firebase/messaging');
        const firebaseConfig = (await import('../firebaseConfig')).default;
        const { VAPID_KEY } = await import('../firebaseConfig');

        const app = initializeApp(firebaseConfig);
        const messaging = getMessaging(app);

        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: await navigator.serviceWorker.register('/firebase-messaging-sw.js'),
        });

        if (token) {
          await sendToken(token, 'web');
          await setupWebForegroundListener();
          return true;
        }
        return false;
      }
    } catch (err) {
      console.error('Error in registerPush:', err);
      return false;
    }
  }, [userId, sendToken, setupWebForegroundListener]);

  // ── Native (Capacitor) push setup ──────────────────────────────────
  // Sets up listeners and auto-registers ONLY if permission is already
  // granted and user hasn't toggled off (no prompt shown on mount).
  useEffect(() => {
    if (!userId || !isNativeApp()) return;

    const ac = new AbortController();
    abortRef.current = ac;
    let cleanup = () => {};

    const setupNativePush = async () => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');

        // Clear any stale listeners from previous mounts
        await PushNotifications.removeAllListeners();

        const platform = window.Capacitor?.getPlatform?.() === 'ios' ? 'ios' : 'android';

        // On Android, the Capacitor plugin returns the FCM token directly.
        // On iOS, it returns the APNs device token — which is NOT what the backend
        // needs (firebase-admin messaging.send() requires an FCM token).
        // For iOS, we get the FCM token from the native AppDelegate bridge instead.
        if (platform !== 'ios') {
          await PushNotifications.addListener('registration', (token) => {
            console.log('Push token received:', token.value?.substring(0, 20) + '...');
            sendToken(token.value, platform);
          });
        }

        const regErrorListener = await PushNotifications.addListener('registrationError', (err) => {
          console.error('Push registration error:', err);
        });

        const receivedListener = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
          console.log('Push received in foreground:', notification);
        });

        const actionListener = await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
          const data = action.notification?.data;
          if (data && onNotificationTap) {
            onNotificationTap(data);
          }
        });

        // Auto-register silently if permission already granted and pref not disabled.
        // Wrap in a tracked promise so manual toggle can await it instead of racing.
        const pref = localStorage.getItem(PREF_KEY);
        if (pref !== 'disabled') {
          const autoReg = (async () => {
            const permResult = await PushNotifications.checkPermissions();
            if (permResult.receive === 'granted') {
              await PushNotifications.register();
              console.log('PushNotifications.register() called (auto)');

              if (platform === 'ios') {
                const fcmToken = await waitForFCMToken(10000, ac.signal);
                if (fcmToken) {
                  console.log('FCM token received from native bridge:', fcmToken.substring(0, 20) + '...');
                  await sendToken(fcmToken, 'ios');
                }
              }
              // Android: registration listener above handles it
            }
          })();
          autoRegisterPromiseRef.current = autoReg;
          await autoReg;
          autoRegisterPromiseRef.current = null;
        }

        cleanup = () => {
          regErrorListener.remove();
          receivedListener.remove();
          actionListener.remove();
        };
      } catch (err) {
        console.error('Error setting up native push:', err);
      }
    };

    setupNativePush();
    return () => {
      ac.abort();
      cleanup();
    };
  }, [userId, sendToken, onNotificationTap]);

  // ── Web push setup (Firebase Messaging) ────────────────────────────
  // Auto-registers ONLY if permission is already granted and user
  // hasn't toggled off (no prompt shown on mount).
  useEffect(() => {
    if (!userId || isNativeApp()) return;

    const setupWebPush = async () => {
      try {
        const pref = localStorage.getItem(PREF_KEY);
        if (pref === 'disabled') return;

        if (!('Notification' in window)) return;
        // Only auto-register if permission already granted — never auto-prompt
        if (Notification.permission !== 'granted') return;

        const { initializeApp } = await import('firebase/app');
        const { getMessaging, getToken } = await import('firebase/messaging');
        const firebaseConfig = (await import('../firebaseConfig')).default;
        const { VAPID_KEY } = await import('../firebaseConfig');

        const app = initializeApp(firebaseConfig);
        const messaging = getMessaging(app);

        const token = await getToken(messaging, {
          vapidKey: VAPID_KEY,
          serviceWorkerRegistration: await navigator.serviceWorker.register('/firebase-messaging-sw.js'),
        });

        if (token) {
          sendToken(token, 'web');
        }

        // Foreground messages
        await setupWebForegroundListener();
      } catch (err) {
        console.error('Error setting up web push:', err);
      }
    };

    setupWebPush();
  }, [userId, sendToken, setupWebForegroundListener]);

  // ── Check actual OS permission state ──────────────────────────────
  // Returns 'granted' | 'denied' | 'prompt' | 'unknown'
  // This is the source of truth — not localStorage.
  const checkOSPermission = useCallback(async () => {
    try {
      if (isNativeApp()) {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const result = await PushNotifications.checkPermissions();
        return result.receive; // 'granted' | 'denied' | 'prompt'
      } else {
        if (!('Notification' in window)) return 'denied';
        // Web Notification.permission is synchronous: 'granted' | 'denied' | 'default'
        const perm = Notification.permission;
        return perm === 'default' ? 'prompt' : perm;
      }
    } catch {
      return 'unknown';
    }
  }, []);

  return { registerPush, unregisterToken, isRegistered, checkOSPermission };
}
